import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/app/api';

/**
 * Универсальный slice для настроек владельца, сгруппированных по SettingsGroup:
 *   tariffs | client_statuses | order_statuses | expenditure | notifications |
 *   funnel_automations | funnel_widgets | finance | telegram_broadcasts
 *
 * Backend-модель: список { key, value } — значение произвольный JSON.
 */

export const SETTINGS_GROUPS = /** @type {const} */ ([
  'tariffs',
  'client_statuses',
  'order_statuses',
  'expenditure',
  'notifications',
  'funnel_automations',
  'funnel_widgets',
  'finance',
  'telegram_broadcasts',
]);

const initialState = {
  byGroup: {},     // { [group]: { items: [], status, error, saveStatus, saveError } }
};

function ensureGroup(state, group) {
  if (!state.byGroup[group]) {
    state.byGroup[group] = {
      items: [],
      status: 'idle',
      error: null,
      saveStatus: 'idle',
      saveError: null,
    };
  }
  return state.byGroup[group];
}

function errMsg(err, fallback) {
  return err.response?.data?.message ?? fallback;
}

export const fetchSettings = createAsyncThunk(
  'settings/fetch',
  async (group, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/owner/settings/${group}`);
      return { group, items: data.items ?? [] };
    } catch (err) {
      return rejectWithValue({ group, message: errMsg(err, 'Ошибка загрузки') });
    }
  },
);

export const createSettingItem = createAsyncThunk(
  'settings/create',
  async ({ group, value, key }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/owner/settings/${group}`, { key, value });
      return { group, item: data.item };
    } catch (err) {
      return rejectWithValue({ group, message: errMsg(err, 'Ошибка сохранения') });
    }
  },
);

export const updateSettingItem = createAsyncThunk(
  'settings/update',
  async ({ group, key, value }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/owner/settings/${group}/${encodeURIComponent(key)}`, {
        value,
      });
      return { group, item: data.item };
    } catch (err) {
      return rejectWithValue({ group, message: errMsg(err, 'Ошибка сохранения') });
    }
  },
);

export const deleteSettingItem = createAsyncThunk(
  'settings/delete',
  async ({ group, key }, { rejectWithValue }) => {
    try {
      await api.delete(`/owner/settings/${group}/${encodeURIComponent(key)}`);
      return { group, key };
    } catch (err) {
      return rejectWithValue({ group, message: errMsg(err, 'Ошибка удаления') });
    }
  },
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearSettingsError(state, action) {
      const g = ensureGroup(state, action.payload);
      g.saveError = null;
      g.saveStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state, action) => {
        const g = ensureGroup(state, action.meta.arg);
        g.status = 'loading';
        g.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        const { group, items } = action.payload;
        const g = ensureGroup(state, group);
        g.status = 'ready';
        g.items = items;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        const payload = action.payload ?? {};
        const g = ensureGroup(state, payload.group ?? action.meta.arg);
        g.status = 'error';
        g.error = payload.message ?? 'Ошибка загрузки';
      })
      .addCase(createSettingItem.pending, (state, action) => {
        const g = ensureGroup(state, action.meta.arg.group);
        g.saveStatus = 'loading';
        g.saveError = null;
      })
      .addCase(createSettingItem.fulfilled, (state, action) => {
        const { group, item } = action.payload;
        const g = ensureGroup(state, group);
        g.saveStatus = 'ready';
        g.items.push(item);
      })
      .addCase(createSettingItem.rejected, (state, action) => {
        const payload = action.payload ?? {};
        const g = ensureGroup(state, payload.group ?? action.meta.arg.group);
        g.saveStatus = 'error';
        g.saveError = payload.message ?? 'Ошибка сохранения';
      })
      .addCase(updateSettingItem.pending, (state, action) => {
        const g = ensureGroup(state, action.meta.arg.group);
        g.saveStatus = 'loading';
        g.saveError = null;
      })
      .addCase(updateSettingItem.fulfilled, (state, action) => {
        const { group, item } = action.payload;
        const g = ensureGroup(state, group);
        g.saveStatus = 'ready';
        const idx = g.items.findIndex((it) => it.key === item.key);
        if (idx >= 0) g.items[idx] = item;
        else g.items.push(item);
      })
      .addCase(updateSettingItem.rejected, (state, action) => {
        const payload = action.payload ?? {};
        const g = ensureGroup(state, payload.group ?? action.meta.arg.group);
        g.saveStatus = 'error';
        g.saveError = payload.message ?? 'Ошибка сохранения';
      })
      .addCase(deleteSettingItem.pending, (state, action) => {
        const g = ensureGroup(state, action.meta.arg.group);
        g.saveStatus = 'loading';
        g.saveError = null;
      })
      .addCase(deleteSettingItem.fulfilled, (state, action) => {
        const { group, key } = action.payload;
        const g = ensureGroup(state, group);
        g.saveStatus = 'ready';
        g.items = g.items.filter((it) => it.key !== key);
      })
      .addCase(deleteSettingItem.rejected, (state, action) => {
        const payload = action.payload ?? {};
        const g = ensureGroup(state, payload.group ?? action.meta.arg.group);
        g.saveStatus = 'error';
        g.saveError = payload.message ?? 'Ошибка удаления';
      });
  },
});

export const { clearSettingsError } = settingsSlice.actions;
export default settingsSlice.reducer;

export const selectSettingsGroup = (group) => (state) =>
  state.settings.byGroup[group] ?? {
    items: [],
    status: 'idle',
    error: null,
    saveStatus: 'idle',
    saveError: null,
  };
